"use client";

import { FC, useEffect, useRef, useState } from "react";
import { findVisibleElement } from "@/lib/tutorial/dom";

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TRACK_INTERVAL_MS = 150;
const RING_PADDING = 6;
/** 画面下の案内 + 下のメニューぶん。これより下にある場所は見える位置まで送る */
const BOTTOM_RESERVED_PX = 280;
const TOP_RESERVED_PX = 64;
/** モーダルが開く動き (antd は約 0.3 秒) が終わってから、もう一度だけ見える位置へ送る */
const REVEAL_RETRY_MS = 700;

interface Props {
  /** 光らせる場所 (CSS セレクタ)。null のときは何も出さない */
  selector: string | null;
  /** 値が変わるたびに、その場所を見える位置まで送り直す (手順が変わった・「場所を見る」を押した) */
  revealKey: string;
}

/**
 * 案内中の手順で「押すところ」を光る枠で囲む。
 * 画面の操作は邪魔しない (枠はクリックを通す)。場所が動いても追いかける。
 */
const Spotlight: FC<Props> = ({ selector, revealKey }) => {
  /** 追いかけている場所 (selector ごと。手順が変わった直後に前の場所の枠を出さないため) */
  const [tracked, setTracked] = useState<{ selector: string; rect: Rect | null } | null>(null);
  /** revealKey ごとに、見える位置へ送った回数 (最初に見つけたとき + 動きが終わったあとの 2 回まで) */
  const revealRef = useRef<{ key: string; count: number; at: number } | null>(null);

  useEffect(() => {
    if (!selector) return;
    let cancelled = false;
    const setRect = (rect: Rect | null) =>
      setTracked((prev) =>
        prev && prev.selector === selector && sameRect(prev.rect, rect)
          ? prev
          : { selector, rect },
      );
    const track = () => {
      if (cancelled) return;
      const el = findVisibleElement(selector);
      if (!el) {
        setRect(null);
        return;
      }
      // 手順が変わって見つかったら、見える位置まで送る (固定のメニュー・ヘッダーは送らない)。
      // モーダルの中の場所は開く動きの途中だと位置がずれるので、少し待ってもう一度だけ送る
      const now = Date.now();
      const reveal = revealRef.current;
      const due =
        !reveal || reveal.key !== revealKey
          ? 0
          : reveal.count === 1 && now - reveal.at >= REVEAL_RETRY_MS
            ? 1
            : -1;
      if (due >= 0) {
        revealRef.current = { key: revealKey, count: due + 1, at: now };
        const r = el.getBoundingClientRect();
        const outOfView =
          r.top < TOP_RESERVED_PX || r.bottom > window.innerHeight - BOTTOM_RESERVED_PX;
        if (outOfView && !el.closest("nav, header")) {
          el.scrollIntoView({ block: "center", behavior: due === 0 ? "smooth" : "auto" });
        }
      }
      // 枠は画面の中に収める (下のメニューのボタンは画面の端まであるので、そのままだと下が切れる)
      const r = el.getBoundingClientRect();
      const top = Math.max(2, r.top - RING_PADDING);
      const left = Math.max(2, r.left - RING_PADDING);
      const bottom = Math.min(window.innerHeight - 2, r.bottom + RING_PADDING);
      const right = Math.min(window.innerWidth - 2, r.right + RING_PADDING);
      setRect({ top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) });
    };
    track();
    const timer = window.setInterval(track, TRACK_INTERVAL_MS);
    window.addEventListener("scroll", track, true);
    window.addEventListener("resize", track);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("scroll", track, true);
      window.removeEventListener("resize", track);
    };
  }, [selector, revealKey]);

  const rect = selector && tracked?.selector === selector ? tracked.rect : null;
  if (!rect) return null;

  return (
    <>
      <style>{`
        @keyframes gc-tutorial-pulse {
          0% { box-shadow: 0 0 0 0 rgba(224, 168, 59, 0.55); }
          70% { box-shadow: 0 0 0 12px rgba(224, 168, 59, 0); }
          100% { box-shadow: 0 0 0 0 rgba(224, 168, 59, 0); }
        }
        .gc-tutorial-spotlight { animation: gc-tutorial-pulse 1.6s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .gc-tutorial-spotlight { animation: none; }
        }
      `}</style>
      <div
        aria-hidden
        className="gc-tutorial-spotlight"
        data-tutorial-spotlight=""
        style={{
          position: "fixed",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: "3px solid #e0a83b",
          borderRadius: 12,
          pointerEvents: "none",
          zIndex: 1065,
          transition: "top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease",
        }}
      />
    </>
  );
};

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

export default Spotlight;
